import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Chip,
  Card,
  CardBody,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useTabParam,
  type SubscriptionStatus,
  holdsPlan,
  dayFirstLocale,
} from '@structural-climbing/ui'
import { MemberSearchInput } from '../features/members/MemberSearchInput'
import { SubscriptionPlanChart } from '../features/members/SubscriptionPlanChart'
import { AssignPlanModal, type RenewTarget } from '../features/members/AssignPlanModal'
import { renewTargetFor } from '../features/members/renewTarget'
import { CreateInvoiceModal } from '../features/members/CreateInvoiceModal'
import { EditPlanModal } from '../features/members/EditPlanModal'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { ChildProfileModal } from '../features/members/ChildProfileModal'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { MemberAvatar } from '../features/members/MemberAvatar'
import { SubBadge } from '../features/members/SubBadge'
import type { GymPlan, Subscription } from '../features/plans/types'
import type { UpcomingPlan } from '../features/members/renewalShift'
import { useCheckIn } from '../features/checkIn/useCheckIn'
import { useCheckOut } from '../features/checkIn/useCheckOut'
import type { ActiveCheckInEntry } from '../features/members/memberTypes'
import { PageTabs } from '../components/PageTabs'
import { PageHeader } from '../components/PageHeader'
import { ListPages } from '../components/ListPages'
import { useIsMobile } from '../lib/useIsMobile'

const STATUS_TAB_KEYS = ['all', 'active', 'expired', 'depleted', 'cancelled'] as const
type StatusTab = (typeof STATUS_TAB_KEYS)[number]
// The list first: it is what the desk opens this page for.
const VIEW_TABS = ['detail', 'chart'] as const
const SOLD_FILTERS = ['any', 'today'] as const

interface SubscriptionRow extends Subscription {
  effective_status: SubscriptionStatus
  /** A renewal is already queued behind this plan: no second Renew. */
  is_renewed?: boolean
  /** That renewal, which editing this plan can move. */
  upcoming_renewal?: UpcomingPlan | null
}

const PAGE_SIZE = 20

/** One page, and the figures above it, worked out over every matching plan. */
interface SubscriptionsPageResponse {
  subscriptions: SubscriptionRow[]
  total: number
  counts: Record<StatusTab, number>
  active_children: number
  sold_today: number
  revenue_total: number
  by_plan: { name: string; count: number }[]
}

const EMPTY_COUNTS: Record<StatusTab, number> = {
  all: 0,
  active: 0,
  expired: 0,
  depleted: 0,
  cancelled: 0,
}

// Day first, in the staff's language (28.09.2026 / 28/09/2026), like every
// date field in the app: "03/04" must not mean two different days.
function formatDate(iso: string | null, language: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(dayFirstLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function isExpiringSoon(expiresAt: string | null, effectiveStatus: SubscriptionStatus): boolean {
  if (effectiveStatus !== 'active' || !expiresAt) return false
  const diff = new Date(expiresAt).getTime() - Date.now()
  return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000
}

function planTypeLabel(planType: string, t: (k: string) => string): string {
  if (planType === 'day_pass') return t('plans.typeDayPass')
  if (planType === 'entry_based') return t('plans.typeEntryBased')
  return t('plans.typeDurationBased')
}

export function SubscriptionsPage() {
  const isMobile = useIsMobile()
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [loading, setLoading] = useState(true)
  // One page from the server: the gym's whole sales history no longer comes
  // down to the browser to be counted and filtered there.
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([])
  const [summary, setSummary] = useState<Omit<SubscriptionsPageResponse, 'subscriptions'>>({
    total: 0,
    counts: EMPTY_COUNTS,
    active_children: 0,
    sold_today: 0,
    revenue_total: 0,
    by_plan: [],
  })
  const [page, setPage] = useState(1)
  const [plans, setPlans] = useState<GymPlan[]>([])
  const [checkedInMap, setCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(new Map())
  const [childCheckedInMap, setChildCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(
    new Map(),
  )

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // In the URL so pull-to-refresh keeps them and the dashboard's "New
  // subscriptions today" card can open the list already narrowed.
  const [viewTab, setViewTab] = useTabParam('view', VIEW_TABS, 'detail')
  const [statusTab, setStatusTab] = useTabParam('status', STATUS_TAB_KEYS, 'active')
  const [sold, setSold] = useTabParam('sold', SOLD_FILTERS, 'any')
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')

  const [assignTarget, setAssignTarget] = useState<{
    memberEmail: string
    memberUserId: string | null
    childId?: string
    childName?: string
    renewOf?: RenewTarget
  } | null>(null)
  const [editTarget, setEditTarget] = useState<{
    subscription: Subscription
    memberEmail: string
    upcoming?: UpcomingPlan | null
  } | null>(null)

  // The renewal waiting behind a plan, which editing the plan can move.
  function upcomingFor(row: SubscriptionRow): UpcomingPlan | null {
    return row.upcoming_renewal ?? null
  }
  const [profileTarget, setProfileTarget] = useState<string | null>(null)
  const [childProfileTarget, setChildProfileTarget] = useState<string | null>(null)
  const [createInvoiceTarget, setCreateInvoiceTarget] = useState<{
    subscriptionId: string
    memberEmail: string
    memberUserId: string | null
    planName: string
    pricePaid: number
    createdAt: string
  } | null>(null)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { checkIn, stateFor } = useCheckIn()
  const reload = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/active-check-ins`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = (await res.json()) as {
        active_check_ins: {
          id: string
          user_id: string | null
          child_id: string | null
          is_guest: boolean
        }[]
      }
      const parentMap = new Map<string, ActiveCheckInEntry>()
      const childMap = new Map<string, ActiveCheckInEntry>()
      for (const c of data.active_check_ins) {
        if (c.child_id) childMap.set(c.child_id, { checkInId: c.id, isGuest: c.is_guest })
        else if (c.user_id) parentMap.set(c.user_id, { checkInId: c.id, isGuest: c.is_guest })
      }
      setCheckedInMap(parentMap)
      setChildCheckedInMap(childMap)
    } catch {
      // non-critical
    }
  }, [getAccessTokenSilently, apiUrl])
  const { checkOut, isCheckingOut, isAlreadyCheckedOut } = useCheckOut(reload)

  useEffect(() => {
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const [plansRes, checkInsRes] = await Promise.all([
          fetch(`${apiUrl}/gym-plans`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/active-check-ins`, { headers: { Authorization: `Bearer ${token}` } }),
        ])
        if (plansRes.ok) setPlans((await plansRes.json()) as GymPlan[])
        if (checkInsRes.ok) {
          const data = (await checkInsRes.json()) as {
            active_check_ins: {
              id: string
              user_id: string | null
              child_id: string | null
              is_guest: boolean
            }[]
          }
          const parentMap = new Map<string, ActiveCheckInEntry>()
          const childMap = new Map<string, ActiveCheckInEntry>()
          for (const c of data.active_check_ins) {
            if (c.child_id) childMap.set(c.child_id, { checkInId: c.id, isGuest: c.is_guest })
            else if (c.user_id) parentMap.set(c.user_id, { checkInId: c.id, isGuest: c.is_guest })
          }
          setCheckedInMap(parentMap)
          setChildCheckedInMap(childMap)
        }
      } catch {
        // non-critical: the list still loads without plans and check-ins
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [search])

  // A newer request replaces any still in flight, so a slow page never lands
  // over the one asked for after it.
  const abortRef = useRef<AbortController | null>(null)
  const fetchPage = useCallback(
    async (pageNumber: number) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String((pageNumber - 1) * PAGE_SIZE),
          status: statusTab,
        })
        if (selectedPlanId) params.set('plan', selectedPlanId)
        if (sold === 'today') params.set('sold', 'today')
        if (debouncedSearch) params.set('search', debouncedSearch)
        const res = await fetch(`${apiUrl}/subscriptions?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        if (!res.ok) return
        const { subscriptions: rows, ...rest } = (await res.json()) as SubscriptionsPageResponse
        setSubscriptions(rows)
        setSummary(rest)
        setPage(pageNumber)
      } catch {
        // Aborted by a newer request, or offline: keep what is on screen.
      } finally {
        if (abortRef.current === controller) setLoading(false)
      }
    },
    [getAccessTokenSilently, apiUrl, statusTab, selectedPlanId, sold, debouncedSearch],
  )

  // Any filter change starts again from the first page.
  useEffect(() => {
    void fetchPage(1)
  }, [fetchPage])

  const tabCounts = summary.counts
  const activeChildCount = summary.active_children
  const soldTodayCount = summary.sold_today
  const visible = subscriptions
  const currency = plans[0]?.currency ?? ''
  const revenueTotal = summary.revenue_total
  const chartData = summary.by_plan

  const STATUS_TABS: { key: StatusTab; label: string }[] = [
    { key: 'all', label: t('subscriptions.filterAll') },
    { key: 'active', label: t('subscriptions.filterActive') },
    { key: 'expired', label: t('subscriptions.filterExpired') },
    { key: 'depleted', label: t('subscriptions.filterDepleted') },
    { key: 'cancelled', label: t('subscriptions.filterCancelled') },
  ]

  function handleModalClose() {
    setAssignTarget(null)
    setEditTarget(null)
  }

  function handleUpdated() {
    handleModalClose()
    reloadSubscriptions()
  }

  // After a sale or an edit: the same page again, with fresh counts.
  function reloadSubscriptions() {
    void fetchPage(page)
  }

  // One subscription's cells, shared by the desktop table and the phone cards.
  function rowParts(row: SubscriptionRow) {
    const renewOf = renewTargetFor(row, !!row.is_renewed)
    const childName = row.child_profiles
      ? [row.child_profiles.first_name, row.child_profiles.last_name].filter(Boolean).join(' ')
      : undefined
    const expiringSoon = isExpiringSoon(row.expires_at, row.effective_status)
    const planName = row.gym_plans?.name ?? '—'
    const planType = row.gym_plans?.plan_type ?? ''
    const memberEmail = getMemberEmail(row)

    return {
      member: <>{renderMemberCell(row)}</>,
      plan: (
        <>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{planName}</span>
            <div className="flex flex-wrap gap-1">
              {planType && (
                <span className="w-fit rounded-full bg-default/20 px-2 py-0.5 text-xs text-foreground/60">
                  {planTypeLabel(planType, t)}
                </span>
              )}
              {row.is_addon && (
                <span className="w-fit rounded-full bg-secondary/10 px-2 py-0.5 text-xs text-secondary">
                  {t('plans.addonChip')}
                </span>
              )}
              {row.renews_subscription_id && (
                <Chip size="sm" variant="flat" color="primary">
                  {row.renewal_discount_percent != null
                    ? t('subscriptions.renewalChipDiscount', {
                        percent: row.renewal_discount_percent,
                      })
                    : t('subscriptions.renewalChip')}
                </Chip>
              )}
              {row.renewal_offer && (
                <Chip size="sm" variant="flat" color="success">
                  {t('members.renewalOfferChip', {
                    percent: row.renewal_offer.percent,
                  })}
                </Chip>
              )}
            </div>
          </div>
        </>
      ),
      status: (
        <>
          <SubBadge status={row.effective_status} />
        </>
      ),
      price: (
        <>
          <span className="text-sm">
            {currency}
            {row.price_paid.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </>
      ),
      start: (
        <>
          <span className="text-sm">{formatDate(row.starts_at, i18n.language)}</span>
        </>
      ),
      expiry: (
        <>
          <span className={`text-sm ${expiringSoon ? 'font-medium text-warning' : ''}`}>
            {formatDate(row.expires_at, i18n.language)}
          </span>
        </>
      ),
      entries: (
        <>
          <span className="text-sm">
            {row.entries_remaining !== null ? String(row.entries_remaining) : '—'}
          </span>
        </>
      ),
      actions: (
        <>
          <div className="flex items-center gap-1">
            {/* A lapsed plan stays editable, so a wrong expiry typed by
                                        mistake can be put right. A cancelled one does not. */}
            {row.effective_status !== 'cancelled' && (
              <Button
                size="sm"
                variant="flat"
                isIconOnly
                aria-label={t('members.editPlan')}
                onClick={() =>
                  setEditTarget({
                    subscription: row,
                    memberEmail,
                    upcoming: upcomingFor(row),
                  })
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-4"
                >
                  <path d="M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z" />
                </svg>
              </Button>
            )}
            {!holdsPlan(row.effective_status) && memberEmail && (
              <Button
                size="sm"
                variant="flat"
                color="primary"
                isIconOnly
                aria-label={t('members.assignPlan')}
                onClick={() =>
                  setAssignTarget({
                    memberEmail,
                    memberUserId: row.user_id,
                    ...(row.child_id ? { childId: row.child_id } : {}),
                    ...(row.child_profiles
                      ? {
                          childName: [row.child_profiles.first_name, row.child_profiles.last_name]
                            .filter(Boolean)
                            .join(' '),
                        }
                      : {}),
                  })
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-4"
                >
                  <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                </svg>
              </Button>
            )}
            {renewOf && memberEmail && (
              <Button
                size="sm"
                variant="flat"
                onClick={() =>
                  setAssignTarget({
                    memberEmail,
                    memberUserId: row.user_id,
                    ...(row.child_id ? { childId: row.child_id, childName } : {}),
                    renewOf,
                  })
                }
              >
                {t('members.renewPlan')}
              </Button>
            )}
            {row.price_paid > 0 && (!row.orders || row.orders.length === 0) && memberEmail && (
              <Button
                size="sm"
                variant="flat"
                color="warning"
                aria-label={t('subscriptions.createInvoice')}
                onClick={() =>
                  setCreateInvoiceTarget({
                    subscriptionId: row.id,
                    memberEmail,
                    memberUserId: row.user_id,
                    planName: row.gym_plans?.name ?? '',
                    pricePaid: row.price_paid,
                    createdAt: row.created_at.slice(0, 10),
                  })
                }
              >
                {t('subscriptions.createInvoice')}
              </Button>
            )}
            {renderCheckInCell(row)}
          </div>
        </>
      ),
    }
  }

  function renderMemberCell(row: SubscriptionRow) {
    const canOpenProfile = !!row.user_id
    const wrapperClass = `flex items-center gap-2.5 ${canOpenProfile ? 'cursor-pointer rounded-lg p-1 hover:bg-default/10 transition-colors' : ''}`
    const p = row.profiles

    if (row.child_id && row.child_profiles) {
      const childName = [row.child_profiles.first_name, row.child_profiles.last_name]
        .filter(Boolean)
        .join(' ')
      const parentLabel = row.profiles?.nickname ?? row.profiles?.email ?? ''
      return (
        <div
          className={wrapperClass}
          onClick={canOpenProfile ? () => setChildProfileTarget(row.child_id) : undefined}
        >
          <MemberAvatar
            userId={row.user_id}
            name={childName}
            avatarExt={null}
            avatarUpdatedAt={null}
          />
          <div className="min-w-0">
            <div className="text-sm font-medium">{childName}</div>
            <div className="text-xs text-foreground/50">
              {t('subscriptions.childOf', { name: parentLabel })}
            </div>
          </div>
        </div>
      )
    }

    const fullName = [p?.first_name, p?.last_name].filter(Boolean).join(' ')

    return (
      <div
        className={wrapperClass}
        onClick={canOpenProfile ? () => setProfileTarget(row.user_id) : undefined}
      >
        <MemberIdentity
          userId={row.user_id}
          name={fullName || p?.email || '?'}
          nickname={p?.nickname}
          email={p?.email}
          avatarExt={null}
          avatarUpdatedAt={null}
          isClubMember={row.flags?.is_club_member}
          size="sm"
        />
      </div>
    )
  }

  function renderCheckInCell(row: SubscriptionRow) {
    if (!row.user_id) return null
    // A queued renewal is not what the member enters on. Check-in state is kept
    // per person, so without this the row would mirror the current plan's button.
    if (row.renews_subscription_id && row.effective_status === 'scheduled') return null
    const isChild = !!row.child_id
    const ciState = isChild ? stateFor(row.user_id, row.child_id!) : stateFor(row.user_id)
    const activeEntry = isChild
      ? childCheckedInMap.get(row.child_id!)
      : checkedInMap.get(row.user_id)
    const alreadyIn = ciState === 'already_in' || !!activeEntry

    if (ciState === 'loading') {
      return (
        <Button size="sm" variant="flat" color="primary" isDisabled>
          <Spinner size="sm" color="current" />
        </Button>
      )
    }
    if (ciState === 'success') {
      return (
        <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          {t('members.checkInSuccess')}
        </span>
      )
    }
    if (alreadyIn && activeEntry) {
      if (isAlreadyCheckedOut(activeEntry.checkInId)) {
        return (
          <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
            {t('dashboard.alreadyCheckedOut')}
          </span>
        )
      }
      return (
        <Button
          size="sm"
          variant="flat"
          color="danger"
          isDisabled={isCheckingOut(activeEntry.checkInId)}
          onClick={() => void checkOut(activeEntry.checkInId, activeEntry.isGuest)}
        >
          {isCheckingOut(activeEntry.checkInId)
            ? t('dashboard.checkingOut')
            : t('dashboard.checkOut')}
        </Button>
      )
    }
    if (alreadyIn) {
      return (
        <Button size="sm" variant="flat" isDisabled>
          {t('members.checkInAlreadyIn')}
        </Button>
      )
    }
    return (
      <Button
        size="sm"
        variant="flat"
        color="primary"
        isDisabled={row.effective_status !== 'active'}
        onClick={() => {
          if (row.user_id) void checkIn(row.user_id, row.child_id ?? undefined)
        }}
      >
        {t('members.manualCheckIn')}
      </Button>
    )
  }

  function getMemberEmail(row: SubscriptionRow): string {
    return row.profiles?.email ?? ''
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('subscriptions.title')} subtitle={t('subscriptions.subtitle')} />

      <div className="mb-4 flex flex-col gap-3">
        <PageTabs
          ariaLabel={t('subscriptions.title')}
          selectedKey={viewTab}
          onSelectionChange={setViewTab}
          className=""
          items={[
            { key: 'detail', label: t('subscriptions.detailViewTab') },
            { key: 'chart', label: t('subscriptions.chartViewTab') },
          ]}
        />

        {viewTab === 'detail' && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <MemberSearchInput
              value={search}
              onValueChange={setSearch}
              className="w-full sm:flex-1"
            />
            <Select
              size="sm"
              aria-label={t('subscriptions.allPlans')}
              placeholder={t('subscriptions.allPlans')}
              selectedKeys={selectedPlanId ? new Set([selectedPlanId]) : new Set<string>()}
              onSelectionChange={(keys) => {
                const val = [...keys][0] as string | undefined
                setSelectedPlanId(val ?? '')
                setStatusTab('all')
              }}
              className="w-full sm:w-52"
            >
              {plans.map((p) => (
                <SelectItem key={p.id}>{p.name}</SelectItem>
              ))}
            </Select>
            {selectedPlanId && (
              <Button
                size="sm"
                variant="flat"
                onClick={() => {
                  setSelectedPlanId('')
                  setStatusTab('all')
                }}
              >
                {t('subscriptions.allPlans')}
              </Button>
            )}
          </div>
        )}

        {/* Status as pills, like the Members filters. Each keeps its count. */}
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] md:flex-wrap">
          {STATUS_TABS.map(({ key, label }) => {
            const count = tabCounts[key]
            const adultCount = count - (key === 'active' ? activeChildCount : 0)
            const suffix =
              key === 'active' && activeChildCount > 0
                ? `${adultCount} + ${activeChildCount} ${activeChildCount === 1 ? t('subscriptions.childSuffix') : t('subscriptions.childrenSuffix')}`
                : String(count)
            const isActive = statusTab === key
            return (
              <Button
                key={key}
                size="sm"
                radius="full"
                variant={isActive ? 'solid' : 'flat'}
                color={isActive ? 'primary' : 'default'}
                className="shrink-0 font-semibold"
                aria-pressed={isActive}
                onClick={() => setStatusTab(key)}
              >
                {label} ({suffix})
              </Button>
            )
          })}
          <Button
            size="sm"
            radius="full"
            variant={sold === 'today' ? 'solid' : 'flat'}
            color={sold === 'today' ? 'primary' : 'default'}
            className="shrink-0 font-semibold"
            aria-pressed={sold === 'today'}
            aria-label={
              sold === 'today'
                ? t('subscriptions.clearSoldToday')
                : `${t('subscriptions.soldToday')} (${soldTodayCount})`
            }
            onClick={() => setSold(sold === 'today' ? 'any' : 'today')}
          >
            {t('subscriptions.soldToday')} ({soldTodayCount})
          </Button>
        </div>
      </div>

      <Card shadow="sm">
        <CardBody className="p-0">
          {loading && subscriptions.length === 0 && summary.total === 0 ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : viewTab === 'chart' ? (
            <div className="px-4 py-4">
              <SubscriptionPlanChart
                data={chartData}
                total={chartData.reduce((acc, d) => acc + d.count, 0)}
              />
            </div>
          ) : visible.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-foreground/50">
              {t('subscriptions.noData')}
            </p>
          ) : isMobile ? (
            <div className="divide-y divide-divider">
              {visible.map((row) => {
                const parts = rowParts(row)
                return (
                  <div key={row.id} className="flex flex-col gap-2 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">{parts.member}</div>
                      {parts.status}
                    </div>
                    {parts.plan}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-foreground/60">
                      {parts.price}
                      <span className="flex items-center gap-1">
                        {parts.start}
                        <span aria-hidden="true">→</span>
                        {parts.expiry}
                      </span>
                      {row.entries_remaining !== null && (
                        <span className="text-sm">
                          {t('subscriptions.colEntries')}: {parts.entries}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-end">{parts.actions}</div>
                  </div>
                )
              })}
            </div>
          ) : (
            <Table
              aria-label={t('subscriptions.title')}
              removeWrapper
              classNames={{ th: 'bg-transparent', td: 'align-middle' }}
            >
              <TableHeader>
                <TableColumn>{t('subscriptions.colMember')}</TableColumn>
                <TableColumn>{t('subscriptions.colPlan')}</TableColumn>
                <TableColumn>{t('subscriptions.colStatus')}</TableColumn>
                <TableColumn>{t('subscriptions.colPrice')}</TableColumn>
                <TableColumn>{t('subscriptions.colStart')}</TableColumn>
                <TableColumn>{t('subscriptions.colExpiry')}</TableColumn>
                <TableColumn>{t('subscriptions.colEntries')}</TableColumn>
                <TableColumn>{t('subscriptions.colActions')}</TableColumn>
              </TableHeader>
              <TableBody>
                {visible.map((row) => {
                  const parts = rowParts(row)
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{parts.member}</TableCell>
                      <TableCell>{parts.plan}</TableCell>
                      <TableCell>{parts.status}</TableCell>
                      <TableCell>{parts.price}</TableCell>
                      <TableCell>{parts.start}</TableCell>
                      <TableCell>{parts.expiry}</TableCell>
                      <TableCell>{parts.entries}</TableCell>
                      <TableCell>{parts.actions}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>
      {viewTab === 'detail' && (
        <ListPages
          page={page}
          pageSize={PAGE_SIZE}
          total={summary.total}
          onPageChange={(next) => void fetchPage(next)}
          isLoading={loading}
        />
      )}
      {viewTab === 'detail' && revenueTotal > 0 && (
        <p className="mt-3 text-right text-sm font-semibold tabular-nums">
          {t('subscriptions.revenueTotal', {
            value: `${currency}${revenueTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          })}
        </p>
      )}

      {assignTarget && (
        <AssignPlanModal
          memberEmail={assignTarget.memberEmail}
          memberUserId={assignTarget.memberUserId}
          childId={assignTarget.childId}
          childName={assignTarget.childName}
          plans={plans}
          renewOf={assignTarget.renewOf}
          onAssigned={handleUpdated}
          onClose={handleModalClose}
        />
      )}

      {editTarget && (
        <EditPlanModal
          subscription={editTarget.subscription}
          memberEmail={editTarget.memberEmail}
          upcoming={editTarget.upcoming}
          plans={plans}
          onUpdated={handleUpdated}
          onDeleted={handleUpdated}
          onClose={handleModalClose}
        />
      )}

      {profileTarget && (
        <MemberProfileModal
          userId={profileTarget}
          onClose={() => setProfileTarget(null)}
          onChanged={reloadSubscriptions}
        />
      )}
      {childProfileTarget && (
        <ChildProfileModal
          childId={childProfileTarget}
          onClose={() => setChildProfileTarget(null)}
          onChanged={reloadSubscriptions}
          onOpenParent={(parentId) => {
            setChildProfileTarget(null)
            setProfileTarget(parentId)
          }}
        />
      )}

      {createInvoiceTarget && (
        <CreateInvoiceModal
          subscriptionId={createInvoiceTarget.subscriptionId}
          memberEmail={createInvoiceTarget.memberEmail}
          memberUserId={createInvoiceTarget.memberUserId}
          planName={createInvoiceTarget.planName}
          defaultAmount={createInvoiceTarget.pricePaid}
          defaultDate={createInvoiceTarget.createdAt}
          currency={currency}
          onCreated={() => {
            setCreateInvoiceTarget(null)
            handleUpdated()
          }}
          onClose={() => setCreateInvoiceTarget(null)}
        />
      )}
    </div>
  )
}
